import { Component, signal, ElementRef, ViewChild, AfterViewInit, ChangeDetectorRef, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { RegisterService } from './service/register.service';
import { NotificationsService } from '../../shared/components/notifications/service/notifications.service';
import { RegisterData, RegisterResponseApi } from './interfaces/register.interface';
import { finalize } from 'rxjs/operators';
import { gsap } from 'gsap';
import { PasswordRegexHandler } from '../../core/handlers/password-regex.handler';

@Component({
  selector: 'features-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.css'
})
export class RegisterComponent implements AfterViewInit {
  registerForm: FormGroup;
  currentStep = 1;
  isLoading = false;
  errorMessage = '';
  showPassword = signal(false);

  @ViewChild('formCard') formCard?: ElementRef<HTMLDivElement>;
  @ViewChild('step1Panel') step1Panel?: ElementRef<HTMLDivElement>;
  @ViewChild('step2Panel') step2Panel?: ElementRef<HTMLDivElement>;


passwordRegex=inject(PasswordRegexHandler);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private registerService: RegisterService,
    private notificationsService: NotificationsService,
    private cd: ChangeDetectorRef
  ) {
    this.registerForm = this.fb.group({
      // Paso 1
      nombre: ['', [Validators.required, Validators.minLength(2), Validators.pattern('^[a-zA-ZáéíóúÁÉÍÓÚñÑ ]+$')]],
      apellidos: ['', [Validators.required, Validators.minLength(2), Validators.pattern('^[a-zA-ZáéíóúÁÉÍÓÚñÑ ]+$')]],
      telefono: ['', [Validators.required, Validators.pattern('^[0-9]+$'), Validators.maxLength(13)]],
      fechaNacimiento: ['', [Validators.required, this.ageValidator]],

      // Paso 2
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator]]
    });
  }

  ngAfterViewInit(): void {
    // Animación inicial del contenedor del formulario
    if (this.formCard?.nativeElement) {
      gsap.from(this.formCard.nativeElement, {
        opacity: 0,
        y: 24,
        duration: 1.2,
        ease: 'power2.out'
      });
    }

    // Entrada suave del paso 1 (más lenta)
    if (this.step1Panel?.nativeElement) {
      gsap.from(this.step1Panel.nativeElement, {
        opacity: 0,
        x: 16,
        duration: 1.0,
        ease: 'power2.out'
      });
    }
  }

  // Validador personalizado para verificar que el usuario sea mayor de 18 años
  ageValidator(control: any) {
    if (!control.value) {
      return null;
    }

    const today = new Date();
    const birthDate = new Date(control.value);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 18 ? null : { underage: true };
  }

  // Validador para verificar que la contraseña tenga al menos una mayúscula y un número
  // Usamos función flecha para preservar el contexto de `this` cuando Angular ejecuta el validador
  passwordStrengthValidator = (control: any) => {
    return this.passwordRegex.passwordStrengthValidator(control);
  }
  nextStep(): void {
    // Validar el formulario del paso actual antes de avanzar
    if (this.currentStep === 1) {
      const step1Controls = ['nombre', 'apellidos', 'telefono', 'fechaNacimiento'];

      if (step1Controls.some(controlName => this.registerForm.get(controlName)?.invalid)) {
        // Marcar todos los campos como tocados para mostrar errores
        step1Controls.forEach(controlName => {
          this.registerForm.get(controlName)?.markAsTouched();
        });
        return;
      }
      // Animar salida del paso 1 y entrada del paso 2
      this.animateStepChange(2);
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.animateStepChange(1);
    }
  }

  onSubmit(): void {
    if (this.registerForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      // Preparar los datos según la interfaz RegisterData
      const registerData: RegisterData = {
        name: this.registerForm.get('nombre')?.value,
        lastname: this.registerForm.get('apellidos')?.value,

        email: this.registerForm.get('email')?.value,
        password: this.registerForm.get('password')?.value,
        phone: +this.registerForm.get('telefono')?.value,
        birthday: this.registerForm.get('fechaNacimiento')?.value
      };

      // Enviar los datos al servidor
      this.registerService.register(registerData)
        .pipe(
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe({
          next: (response: RegisterResponseApi) => {
            if (response.status) {
              // Mostrar notificación de éxito
              this.notificationsService.addNotification({
                message: 'Registro exitoso, verifica tu correo',
                type: 'success',
                duration: 5000
              });

              // Redirigir al login después del registro exitoso
              setTimeout(() => {
                this.router.navigate(['/auth/login']);
              }, 1500);
            } else {
              this.notificationsService.addNotification({
                message: response.message,
                type: "error",
                duration: 5000
              });
            }
            this.registerForm.reset();
          },
          error: (error) => {
            // console.log(error);
          }
        });
    } else {
      // Marcar todos los campos como tocados para mostrar errores
      Object.keys(this.registerForm.controls).forEach(key => {
        this.registerForm.get(key)?.markAsTouched();
      });
    }
  }

  toggleShowPassword() {
    this.showPassword.set(!this.showPassword());
  }

  private animateStepChange(toStep: number) {
    if (toStep === 2 && this.step1Panel?.nativeElement) {
      gsap.to(this.step1Panel.nativeElement, {
        x: -20,
        opacity: 0,
        duration: 0.8,
        ease: 'power2.in',
        onComplete: () => {
          this.currentStep = 2;
          this.cd.detectChanges();
          const el2 = this.step2Panel?.nativeElement;
          if (el2) {
            gsap.fromTo(el2, { x: 20, opacity: 0 }, {
              x: 0,
              opacity: 1,
              duration: 0.9,
              ease: 'power2.out'
            });
          }
        }
      });
    } else if (toStep === 1 && this.step2Panel?.nativeElement) {
      gsap.to(this.step2Panel.nativeElement, {
        x: 20,
        opacity: 0,
        duration: 0.8,
        ease: 'power2.in',
        onComplete: () => {
          this.currentStep = 1;
          this.cd.detectChanges();
          const el1 = this.step1Panel?.nativeElement;
          if (el1) {
            gsap.fromTo(el1, { x: -20, opacity: 0 }, {
              x: 0,
              opacity: 1,
              duration: 0.9,
              ease: 'power2.out'
            });
          }
        }
      });
    }
  }

  // Getters para acceder fácilmente a los controles del formulario en la plantilla
  get step1Valid() {
    const step1Controls = ['nombre', 'apellidos', 'telefono', 'fechaNacimiento'];
    return step1Controls.every(controlName => this.registerForm.get(controlName)?.valid);
  }
  get nombre() { return this.registerForm.get('nombre'); }
  get apellidos() { return this.registerForm.get('apellidos'); }
  get telefono() { return this.registerForm.get('telefono'); }
  get fechaNacimiento() { return this.registerForm.get('fechaNacimiento'); }
  get email() { return this.registerForm.get('email'); }
  get password() { return this.registerForm.get('password'); }
}
